import React, { useEffect, useRef, useState } from "react";

import { Dropdown, Form } from "react-bootstrap";

import classNames from "classnames";
import type { ControllerRenderProps, FieldValues, Path } from "react-hook-form";

import styles from "./MultiSelectDropdown.module.scss";

interface Option {
  value: string;
  label?: string;
}

interface MultiSelectDropdownProps<TFieldValues extends FieldValues> {
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  options: Option[];
  isDisabled?: boolean;
  isInvalid?: boolean;
  placeholder?: string;
}

const MultiSelectDropdown = <TFieldValues extends FieldValues>({
  field,
  options,
  isDisabled = false,
  isInvalid = false,
  placeholder = "Select options",
}: MultiSelectDropdownProps<TFieldValues>) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = (nextShow: boolean) => {
    setIsOpen(nextShow);
  };

  // prevent dropdown from showing when clicking outside
  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const getSelectedLabels = () => {
    return options
      .filter((option) => (field.value as string[])?.includes(option.value))
      .map((option) => option.label || option.value)
      .join(", ");
  };

  return (
    <Dropdown
      ref={dropdownRef}
      show={isOpen}
      onToggle={handleToggle}
      className={classNames(
        styles["multi-select-dropdown"],
        "customized-dropdown",
        field.value?.length > 0 && field.value[0] !== null
          ? styles["multi-select-dropdown-selected"]
          : styles["multi-select-dropdown-placeholder"],
      )}
    >
      <Dropdown.Toggle variant="none" className="customized-dropdown-toggle w-100" disabled={isDisabled}>
        {field.value?.length > 0 && field.value[0] !== null ? getSelectedLabels() : placeholder}
      </Dropdown.Toggle>

      <Dropdown.Menu className={styles["custom-dropdown-menu"]}>
        {options.map((option) => (
          <Form.Check
            className={styles["dropdown-item"]}
            key={option.value}
            type="checkbox"
            label={option.label || option.value}
            value={option.value}
            checked={(field.value as string[])?.includes(option.value) || false}
            disabled={isDisabled}
            isInvalid={isInvalid}
            onChange={(e) => {
              const newValue = e.target.checked
                ? [...(field.value || []), option.value]
                : (field.value || []).filter((v: string) => v !== option.value);
              field.onChange(newValue);
            }}
          />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default MultiSelectDropdown;
